import "./SimulationsTab.css";
import React, {
  createContext,
  useCallback,
  useEffect,
  useState,
  useRef,
} from "react";
import SimulationsControls from "./SimulationsControls";
import useStore from "../../../../Store/Store";

import { SimulationsBody } from "./SimulationsBody";
import { theme } from "../../../../Themes/Themes";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { SimulationsEditNameDelete } from "./SimulationsEditNameDelete";
import { toggleCellsVisibility } from "./SimulationUtils";
import {
  discardRecordedActions,
  saveRecordedActions,
} from "../../../../Store/UndoManager";
import { resetTemporaryStyles } from "../../../DiagramHighlight/DiagramHighlight";
import SimulationCard from "./SimulationCard";
import SimulationBodyCard from "./SimulationBodyCard";
import { Typography } from "@mui/material";
import CustomUsecaseItem from "../AITab/CustomUsecaseItem";
import {
  getRelevantFilesFromKeywords,
  getSimulationDiagram,
  getTopXReadmeFiles,
} from "../../../OnboardingWorkflow/OnboardingWorkflow";
import { logger } from "../../../../utils/Logger";
import { v4 as uuidv4 } from "uuid";
import {
  getURLParam,
  removeURLParam,
  setURLParam,
} from "../../../../utils/URLUtils";
import { durationTracker } from "../../../../utils/DurationTracker";
import {
  needsDiagramGeneration,
  generateDiagramFromSimSteps,
  abortDiagramGeneration,
} from "./SimulationDiagramGenerator";
import { aiWorkflowLogger } from "../../../../utils/AIWorkflowLogger";
import { updateReverseMappingsForSimulation } from "../../../../utils/SimulationMappingUtils";

interface ISimulationsTab {
  selectedTab: number;
  isMaxSD: boolean;
  setIsMaxSD: Function;
}

export const INITIAL_SIMULATIONS_STATE: SimulationsState = {
  simulations: {},
  isEditingSimulations: "",
  currentStep: "",
  selectedSimulationKey: "",
  cellsSetToCorrespondingVisiblityState: false,
  isRunningSimulationNotifId: null,
  cellsWithUnsavedVisibilityFlag: {},
};

export const INITIAL_MODIFIED_SIMULATION_STATE: Simulation = {
  name: "",
  simSteps: [],
  simulationNodesAndEdges: {},
  description: "",
  isAIGenerated: false,
  keywords: "",
};
export interface IApplyCellsStyle {
  cellIds?: string[];
  style?: object;
  options?: {
    removeTemporaryStyles?: boolean;
    lockDiagram?: boolean;
    temporaryStyleChange?: boolean;
    permanentStyleChange?: boolean;
  };
}
type ISimulationsContext = {
  isRunning: boolean;
  isNewSimulation: boolean;
  isMaxSD: boolean;
  currentStep: string;
  prev: string;
  selectedTab: number;
  isSettingSimulationName: boolean;
  simulationNameError: string;
  currentUnsavedSimulationObject: Simulation;
  wiki: string;
  setWiki: React.Dispatch<React.SetStateAction<string>>;
  setIsSettingSimulationName: React.Dispatch<React.SetStateAction<boolean>>;
  setSimulationNameError: React.Dispatch<React.SetStateAction<string>>;
  setIsRunning: React.Dispatch<React.SetStateAction<boolean>>;
  setIsNewSimulation: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentStep: (step: any) => void;
  setPrev: React.Dispatch<React.SetStateAction<string>>;
  setIsMaxSD: Function;
  saveModifiedSimulation: Function;
  simulationDescription: string;
  setSimulationDescription: React.Dispatch<React.SetStateAction<string>>;
  handleCreateSimulation: () => Promise<void>;
};

export const SimulationsContext = createContext<
  ISimulationsContext | undefined
>(undefined);

// Helper function to convert boolean to 1 or 0 for isEdge
const convertIsEdge = (stepType: string): 1 | 0 => {
  return stepType === "edge" ? 1 : 0;
};

// Helper function to map AI response to SimStep format
const mapAIResponseToSimSteps = (aiSteps: any[]): SimStep[] => {
  return aiSteps.map((step) => {
    const isEdge = convertIsEdge(step.stepType);

    // Use clean description without appending examples
    const cleanDescription = step.stepDescription || "";

    return {
      simStepId: uuidv4(),
      diagramNodeId: "", // Leave as placeholder for now as specified
      simStepLabel: step.stepName,
      simStepDescription: cleanDescription,
      isEdge: isEdge,
      sourceCodeMapping: {
        filePath: step.sourceCodeMapping.filePath,
        startLine: String(step.sourceCodeMapping.startLine), // Convert to string
        endLine: String(step.sourceCodeMapping.endLine), // Convert to string
        relevantVariables: step.sourceCodeMapping.relevantVariables,
      },
      inputDataExample: step.inputDataExample,
      outputDataExample: step.outputDataExample,
    };
  });
};

export const SimulationsTab: React.FC<ISimulationsTab> = (
  props: ISimulationsTab
) => {
  const {
    simulations,
    setSimulations,
    currentUnsavedSimulationObject,
    setCurrentUnsavedSimulationObject,
    repoData,
    currentPath,
    setWikiContent,
    isEditingSimulations,
    currentStep,
    simulationsState,
    setSimulationsState,
    selectedSimulationKey,
    selectedCell,
    setSelectedSimulationKey,
    loadSimulationByName,
    hasShownWelcomeDialog,
    setDialog,
    setHasShownWelcomeDialog,
    setTriggerOnboardingWorkflow,
    triggerOnboardingWorkflow,
    onboardingWorkflowState,
    isNewDiagramFileCreated,
    isSimulationFullSizeMode,
    setIsSimulationFullSizeMode,
    isRunningSimulation,
    setIsRunningSimulation,
  } = useStore((state) => ({
    simulations: state.simulationsState.simulations,
    setSimulations: state.setSimulations,
    currentUnsavedSimulationObject: state.currentUnsavedSimulationObject,
    setCurrentUnsavedSimulationObject: state.setCurrentUnsavedSimulationObject,
    repoData: state.repoData,
    currentPath: state.currentPath,
    setWikiContent: state.setWikiContent,
    isEditingSimulations: state.simulationsState.isEditingSimulations,
    currentStep: state.simulationsState.currentStep,
    simulationsState: state.simulationsState,
    setSimulationsState: state.setSimulationsState,
    selectedSimulationKey: state.simulationsState.selectedSimulationKey,
    selectedCell: state.selectedCell,
    setSelectedSimulationKey: state.setSelectedSimulationKey,
    loadSimulationByName: state.loadSimulationByName,
    hasShownWelcomeDialog: state.hasShownWelcomeDialog,
    setDialog: state.setDialog,
    setHasShownWelcomeDialog: state.setHasShownWelcomeDialog,
    setTriggerOnboardingWorkflow: state.setTriggerOnboardingWorkflow,
    triggerOnboardingWorkflow: state.triggerOnboardingWorkflow,
    onboardingWorkflowState: state.onboardingWorkflowState,
    isNewDiagramFileCreated: state.isNewDiagramFileCreated,
    isSimulationFullSizeMode: state.isSimulationFullSizeMode,
    setIsSimulationFullSizeMode: state.setIsSimulationFullSizeMode,
    isRunningSimulation: state.isRunningSimulation,
    setIsRunningSimulation: state.setIsRunningSimulation,
  }));

  const { isMaxSD, setIsMaxSD, selectedTab } = props;
  const [prev, setPrev] = useState("");
  // Use global store for isRunning state
  const isRunning = isRunningSimulation;
  const setIsRunning = setIsRunningSimulation;
  const [isNewSimulation, setIsNewSimulation] = useState(false);
  const [isSettingSimulationName, setIsSettingSimulationName] = useState(false);
  const [simulationNameError, setSimulationNameError] = useState("");
  const [isTransitioning, setIsTransitioning] = useState(false);
  const prevTabRef = useRef(selectedTab);
  const prevSelectedSimulationKeyRef = useRef(selectedSimulationKey);

  // Add state for diagram generation loading
  const [isGeneratingDiagram, setIsGeneratingDiagram] = useState(false);
  const [diagramGenerationStartTime, setDiagramGenerationStartTime] = useState<
    number | undefined
  >(undefined);

  const [simulationDescription, setSimulationDescription] = useState(
    currentUnsavedSimulationObject.description || ""
  );
  const [wiki, setWiki] = useState(repoData[currentPath]?.wiki || "");

  useEffect(() => {
    if (
      simulations &&
      Object.keys(simulations).length > 0 &&
      selectedTab === 3
    ) {
      useStore
        .getState()
        .setSuccessNotification("Select a simulation to view on diagram");
    }
  }, [simulations, selectedTab]);

  useEffect(() => {
    const { setHideIrrelevantCells } = useStore.getState();
    setHideIrrelevantCells(isRunning);
  }, [isRunning]);

  const saveModifiedSimulation = async () => {
    let newSimulationsWithCurrentOneRemoved = Object.fromEntries(
      Object.entries(simulations).filter(
        ([key]) => key !== selectedSimulationKey
      )
    );
    newSimulationsWithCurrentOneRemoved[currentUnsavedSimulationObject.name] =
      currentUnsavedSimulationObject;
    await setSelectedSimulationKey("");
    await useStore.getState().setHideIrrelevantCells(false);
    await resetTemporaryStyles();
    await setSimulations(newSimulationsWithCurrentOneRemoved);
    await setSelectedSimulationKey(currentUnsavedSimulationObject.name);
    await setWikiContent(currentPath, wiki);
    await saveRecordedActions("SIMULATION_MODIFIED");
  };

  // Update URL when simulation state changes
  useEffect(() => {
    const prevSelectedSimulationKey = prevSelectedSimulationKeyRef.current;

    if (selectedSimulationKey) {
      setURLParam(window, "simulation", selectedSimulationKey);
    } else if (!selectedSimulationKey && prevSelectedSimulationKey) {
      // Only remove URL param if we had a simulation selected before and now we don't
      removeURLParam(window, "simulation");
    }

    prevTabRef.current = selectedTab;
    prevSelectedSimulationKeyRef.current = selectedSimulationKey;
  }, [selectedSimulationKey]);

  // Effect to manage full-size mode
  useEffect(() => {
    const shouldEnterFullSize = isRunning && !!selectedSimulationKey;

    if (shouldEnterFullSize !== isSimulationFullSizeMode) {
      setIsSimulationFullSizeMode(shouldEnterFullSize);
      setIsMaxSD(shouldEnterFullSize);
    }
  }, [
    isRunning,
    selectedSimulationKey,
    isSimulationFullSizeMode,
    setIsSimulationFullSizeMode,
    setIsMaxSD,
  ]);

  // Auto-maximize source doc when simulation is selected (unless user manually toggled)
  useEffect(() => {
    const userManuallyToggledSDMaximize =
      useStore.getState().userManuallyToggledSDMaximize;
    if (selectedSimulationKey && !isMaxSD && !userManuallyToggledSDMaximize) {
      logger.info(
        `AUTO_MAXIMIZE: Maximizing source doc for selected simulation: ${selectedSimulationKey}`
      );
      setIsMaxSD(true);
    }
  }, [selectedSimulationKey, isMaxSD, setIsMaxSD]);

  const handleCreateSimulation = async () => {
    await toggleCellsVisibility(false);
    await setSelectedSimulationKey("");
    setIsSettingSimulationName(true);
    setIsNewSimulation(true);
    await setCurrentUnsavedSimulationObject(INITIAL_MODIFIED_SIMULATION_STATE);
    await discardRecordedActions("CURRENT_UNSAVED_SIMULATION_OBJECT");
  };

  // Handler for generating usecases with LLM
  const handleGenerateUsecasesClick = () => {
    if (triggerOnboardingWorkflow) {
      // If already triggered, don't trigger again
      logger.info(
        "OnboardingWorkflow: Already triggered, ignoring duplicate click"
      );
      return;
    }
    logger.info("OnboardingWorkflow: Triggering usecase generation");
    setTriggerOnboardingWorkflow(true);
  };

  // Check if there are any AI-generated simulations
  const hasAIGeneratedSimulations = Object.values(simulations).some(
    (simulation) => simulation.isAIGenerated
  );

  // AI workflow should run if there are no AI-generated simulations AND it's manually triggered,
  // OR if there are no simulations at all
  const shouldRunAIWorkflow =
    (triggerOnboardingWorkflow && !hasAIGeneratedSimulations) ||
    Object.keys(simulations).length === 0;

  // Show generate button only if no AI-generated simulations exist
  const showGenerateButton = !hasAIGeneratedSimulations;

  // Sets "currentStep" in Zustand's state.simulationsState.currentStep
  const setCurrentStep = useCallback(
    (step) => {
      setSimulationsState({
        ...useStore.getState().simulationsState,
        currentStep: step,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSimulationsState, simulationsState, selectedSimulationKey]
  );

  // Function to trigger simulation generation workflow
  const triggerSimulationGeneration = useCallback(
    async (simulation: Simulation) => {
      // Prevent multiple clicks during generation (local state check)
      if (isGeneratingDiagram) {
        logger.debug.info(
          "DEBUGGING_AI_QUALITY: Simulation Generation - Already Running",
          {
            simulationName: simulation.name,
            isAIGenerated: simulation.isAIGenerated,
            hasKeywords: !!simulation.keywords,
            currentStepsCount: simulation.simSteps.length,
            timestamp: new Date().toISOString(),
          }
        );

        logger.warn(
          `❌ SKIPPING - Already generating diagram, cannot start new generation`
        );
        return;
      }

      // Check if this simulation needs AI-generated steps
      if (
        simulation?.isAIGenerated &&
        simulation.keywords &&
        simulation.simSteps.length === 0
      ) {
        // Initialize AI workflow logging for this simulation generation
        aiWorkflowLogger.initializeWorkflow(`simulation_${Date.now()}`);
        aiWorkflowLogger.logStep(
          "Simulation Generation - Starting",
          {
            simulationName: simulation.name,
            keywords: simulation.keywords,
            description: simulation.description,
          },
          "simulation"
        );
        logger.debug.info(
          "DEBUGGING_AI_QUALITY: Simulation Generation - Starting AI Generation",
          {
            simulationName: simulation.name,
            keywords: simulation.keywords,
            description: simulation.description,
            hasExistingSteps: simulation.simSteps.length > 0,
            timestamp: new Date().toISOString(),
          }
        );

        // Start duration tracking for simulation generation
        const sessionId = `simulation-generation-${
          simulation.name
        }-${Date.now()}`;
        durationTracker.startSession(
          sessionId,
          `Simulation Generation for "${simulation.name}"`
        );

        // Flag to track if we successfully generated simsteps and started workaround
        let simstepsGeneratedSuccessfully = false;

        try {
          // Start loading indicator
          setIsGeneratingDiagram(true);
          setDiagramGenerationStartTime(Date.now());

          // Get current state values to avoid dependency issues
          const currentSimulations =
            useStore.getState().simulationsState.simulations;
          const currentRepoTree = useStore.getState().repoTree;

          // Create usecase feature object from simulation data
          const usecaseFeature = {
            usecaseTitle: simulation.name,
            usecaseKeywords: simulation.keywords,
            usecaseDescription: simulation.description || "",
          };

          logger.debug.info(
            "DEBUGGING_AI_QUALITY: Simulation Generation - Usecase Feature Created",
            {
              usecaseFeature: usecaseFeature,
              repoTreeExists: !!currentRepoTree,
              timestamp: new Date().toISOString(),
            }
          );

          // Step 1: Get relevant files for the usecase keywords
          const relevantFiles = await getRelevantFilesFromKeywords(
            usecaseFeature.usecaseKeywords
          );

          logger.debug.info(
            "DEBUGGING_AI_QUALITY: Simulation Generation - Relevant Files Retrieved",
            {
              simulationName: simulation.name,
              keywords: usecaseFeature.usecaseKeywords,
              filesFound: relevantFiles.repoDataObjects.length,
              fileNames: relevantFiles.fileNames,
              totalTokens: relevantFiles.repoDataObjects.reduce(
                (sum, f) =>
                  sum + (f.snippetTokenCount || f.originalTokenCount || 0),
                0
              ),
              timestamp: new Date().toISOString(),
            }
          );

          if (relevantFiles.repoDataObjects.length > 0) {
            // Step 2: Generate simulation diagram using the relevant files
            // Get repoTree and readmeFiles from store
            const readmeFiles = await getTopXReadmeFiles(4);

            logger.debug.info(
              "DEBUGGING_AI_QUALITY: Simulation Generation - README Files Retrieved",
              {
                simulationName: simulation.name,
                readmeFilesCount: readmeFiles.length,
                readmeFiles: readmeFiles.map((f) => f.path),
                timestamp: new Date().toISOString(),
              }
            );

            // Milestone: About to send request to chat completion API
            durationTracker.addMilestone(
              sessionId,
              "About to send request to chat completion API"
            );

            const simulationSteps = await getSimulationDiagram(
              relevantFiles.repoDataObjects,
              usecaseFeature,
              currentRepoTree,
              readmeFiles
            );

            // Milestone: Chat completion API response received
            durationTracker.addMilestone(
              sessionId,
              "Chat completion API response received"
            );

            logger.debug.info(
              "DEBUGGING_AI_QUALITY: Simulation Generation - LLM Response Processing",
              {
                simulationName: simulation.name,
                stepsGenerated: simulationSteps?.length || 0,
                generationSuccess:
                  !!simulationSteps && simulationSteps.length > 0,
                timestamp: new Date().toISOString(),
              }
            );

            if (simulationSteps && simulationSteps.length > 0) {
              // Map AI response to SimStep format
              const mappedSimSteps = mapAIResponseToSimSteps(simulationSteps);

              logger.debug.info(
                "DEBUGGING_AI_QUALITY: Simulation Generation - Steps Mapped",
                {
                  simulationName: simulation.name,
                  originalSteps: simulationSteps.length,
                  mappedSteps: mappedSimSteps.length,
                  stepTypes: mappedSimSteps.map((s) =>
                    s.isEdge ? "edge" : "node"
                  ),
                  sourceFiles: [
                    ...new Set(
                      mappedSimSteps
                        .map((s) => s.sourceCodeMapping?.filePath)
                        .filter(Boolean)
                    ),
                  ],
                  avgLineRange:
                    mappedSimSteps
                      .filter(
                        (s) =>
                          s.sourceCodeMapping?.startLine &&
                          s.sourceCodeMapping?.endLine
                      )
                      .reduce((sum, s) => {
                        const start = parseInt(s.sourceCodeMapping.startLine);
                        const end = parseInt(s.sourceCodeMapping.endLine);
                        return sum + (end - start + 1);
                      }, 0) /
                    Math.max(
                      mappedSimSteps.filter(
                        (s) => s.sourceCodeMapping?.startLine
                      ).length,
                      1
                    ),
                  timestamp: new Date().toISOString(),
                }
              );

              // Update the simulation with the new simsteps - SIMPLIFIED APPROACH
              const updatedSimulation = {
                ...simulation,
                simSteps: mappedSimSteps,
              };

              // SIMPLIFIED UPDATE: Update store simulations directly
              const updatedSimulations = {
                ...currentSimulations,
                [simulation.name]: updatedSimulation,
              };

              await useStore.getState().setSimulations(updatedSimulations);

              // Add reverse mappings for all the new simulation steps
              updateReverseMappingsForSimulation(updatedSimulation);

              // Optionally set the first step as current if there are steps
              if (mappedSimSteps.length > 0) {
                useStore.getState().setSimulationsState({
                  ...useStore.getState().simulationsState,
                  currentStep: mappedSimSteps[0].simStepId,
                });
              }

              // Milestone: Simsteps displayed
              durationTracker.completeSession(
                sessionId,
                "Simsteps displayed and available to user"
              );

              logger.debug.info(
                "DEBUGGING_AI_QUALITY: Simulation Generation - Completed Successfully",
                {
                  simulationName: simulation.name,
                  finalStepsCount: mappedSimSteps.length,
                  firstStepSelected:
                    mappedSimSteps.length > 0
                      ? mappedSimSteps[0].simStepId
                      : null,
                  timestamp: new Date().toISOString(),
                }
              );

              // Mark that simsteps were generated successfully
              simstepsGeneratedSuccessfully = true;

              // Now check if diagram generation is needed for these new steps
              if (needsDiagramGeneration(updatedSimulation)) {
                logger.info("Generating diagram cells for AI simulation");

                const diagramGenerationPromise = generateDiagramFromSimSteps(
                  updatedSimulation
                ).catch((error) => {
                  logger.error("Error during diagram generation:", error);
                  // Return resolved promise even on error so Promise.all doesn't fail
                  return Promise.resolve();
                });

                // Wait for both minimum delay AND diagram generation to complete
                Promise.all([diagramGenerationPromise]).then(async () => {
                  logger.debug.info(
                    "Diagram generation completed, applying close/reload workaround"
                  );

                  // Check if this is during onboarding flow for the first AI simulation
                  const {
                    shouldShowSimulationOnboarding,
                    firstAISimulationName,
                  } = useStore.getState();
                  const isOnboardingFirstSimulation =
                    shouldShowSimulationOnboarding &&
                    simulation.name === firstAISimulationName;

                  if (isOnboardingFirstSimulation) {
                    // During onboarding, close the simulation instead of showing it
                    await useStore.getState().setSelectedSimulationKey("");
                    setIsRunning(false);
                    // Mark onboarding as ready to start
                    useStore.getState().setIsSimulationOnboardingActive(true);
                    // Stop the loading indicator
                    setIsGeneratingDiagram(false);
                    setDiagramGenerationStartTime(undefined);
                    logger.debug.info(
                      "Onboarding: Closed first simulation after simsteps loaded, ready for onboarding wizard"
                    );
                  } else {
                    // RELOAD WORKAROUND: Close and reload simulation to ensure updated diagramNodeIds are displayed
                    // 1. Close the simulation
                    await useStore.getState().setSelectedSimulationKey("");

                    // Small delay to ensure state settles
                    await new Promise((resolve) => setTimeout(resolve, 100));

                    // 2. Reload the simulation with updated data (this updates currentUnsavedSimulationObject)
                    useStore.getState().loadSimulationByName(simulation.name);

                    // 3. Set isRunning to false to show overview
                    setIsRunning(false);

                    // 4. Toggle hideIrrelevantCells to true
                    const showHideToggle =
                      useStore.getState().showHideIrrelevantCellsToggle;
                    if (!showHideToggle) {
                      useStore
                        .getState()
                        .setShowHideIrrelevantCellsToggle(true);
                    }

                    // 5. Finally stop the loading indicator
                    setIsGeneratingDiagram(false);
                    setDiagramGenerationStartTime(undefined);

                    logger.debug.info("Reload workaround complete");
                  }
                });
              } else {
                // If no diagram generation needed, just apply workaround after minimum delay
                setTimeout(async () => {
                  logger.debug.info(
                    "No diagram generation needed, applying close/reload workaround after minimum delay"
                  );

                  // Check if this is during onboarding flow for the first AI simulation
                  const {
                    shouldShowSimulationOnboarding,
                    firstAISimulationName,
                  } = useStore.getState();
                  const isOnboardingFirstSimulation =
                    shouldShowSimulationOnboarding &&
                    simulation.name === firstAISimulationName;

                  if (isOnboardingFirstSimulation) {
                    // During onboarding, close the simulation instead of showing it
                    await useStore.getState().setSelectedSimulationKey("");
                    setIsRunning(false);
                    // Mark onboarding as ready to start
                    useStore.getState().setIsSimulationOnboardingActive(true);
                    // Stop the loading indicator
                    setIsGeneratingDiagram(false);
                    setDiagramGenerationStartTime(undefined);
                    logger.debug.info(
                      "Onboarding: Closed first simulation after simsteps loaded (no generation), ready for onboarding wizard"
                    );
                  } else {
                    // RELOAD WORKAROUND: Close and reload simulation to ensure updated diagramNodeIds are displayed
                    // 1. Close the simulation
                    await useStore.getState().setSelectedSimulationKey("");

                    // Small delay to ensure state settles
                    await new Promise((resolve) => setTimeout(resolve, 100));

                    // 2. Reload the simulation with updated data (this updates currentUnsavedSimulationObject)
                    useStore.getState().loadSimulationByName(simulation.name);

                    // 3. Set isRunning to false to show overview
                    setIsRunning(false);

                    // 4. Toggle hideIrrelevantCells to true
                    const showHideToggle =
                      useStore.getState().showHideIrrelevantCellsToggle;
                    if (!showHideToggle) {
                      useStore
                        .getState()
                        .setShowHideIrrelevantCellsToggle(true);
                    }

                    // 5. Finally stop the loading indicator
                    setIsGeneratingDiagram(false);
                    setDiagramGenerationStartTime(undefined);

                    logger.debug.info(
                      "Reload workaround complete (no generation)"
                    );
                  }
                }, 3000);
              }
            } else {
              logger.debug.info(
                "DEBUGGING_AI_QUALITY: Simulation Generation - No Steps Generated",
                {
                  simulationName: simulation.name,
                  keywords: usecaseFeature.usecaseKeywords,
                  relevantFilesCount: relevantFiles.repoDataObjects.length,
                  timestamp: new Date().toISOString(),
                }
              );

              // Complete session even if no steps were generated
              durationTracker.completeSession(
                sessionId,
                "No simsteps generated"
              );
            }
          } else {
            logger.debug.info(
              "DEBUGGING_AI_QUALITY: Simulation Generation - No Relevant Files Found",
              {
                simulationName: simulation.name,
                keywords: usecaseFeature.usecaseKeywords,
                timestamp: new Date().toISOString(),
              }
            );

            // Complete session if no relevant files found
            durationTracker.completeSession(
              sessionId,
              "No relevant files found"
            );
          }
        } catch (error) {
          logger.debug.info(
            "DEBUGGING_AI_QUALITY: Simulation Generation - Error",
            {
              simulationName: simulation.name,
              error: error.message,
              stack: error.stack,
              timestamp: new Date().toISOString(),
            }
          );

          // Handle error and complete session
          durationTracker.completeSession(
            sessionId,
            "Error occurred during generation"
          );
          logger.error("Error during simulation generation:", error);
        } finally {
          // Only turn off loading indicator if simsteps generation didn't succeed
          // (If it succeeded, the workaround logic will handle it)
          if (!simstepsGeneratedSuccessfully) {
            setIsGeneratingDiagram(false);
            setDiagramGenerationStartTime(undefined);

            logger.debug.info(
              "DEBUGGING_AI_QUALITY: Simulation Generation - Cleanup",
              {
                simulationName: simulation.name,
                generationSuccessful: simstepsGeneratedSuccessfully,
                timestamp: new Date().toISOString(),
              }
            );
          }

          // Finalize AI workflow logging for this simulation generation
          try {
            aiWorkflowLogger.finalizeWorkflow();
          } catch (e) {
            console.error("Failed to finalize AI workflow logger", e);
          }
        }
      } else if (simulation?.isAIGenerated && simulation.simSteps.length > 0) {
        logger.debug.info(
          "DEBUGGING_AI_QUALITY: Simulation Generation - AI Simulation With Existing Steps",
          {
            simulationName: simulation.name,
            existingStepsCount: simulation.simSteps.length,
            needsDiagramGeneration: needsDiagramGeneration(simulation),
            timestamp: new Date().toISOString(),
          }
        );

        // If it's an AI-generated simulation with existing steps, check if diagram generation is needed
        if (needsDiagramGeneration(simulation)) {
          logger.info("Generating diagram cells for AI simulation");
          // Start diagram generation in the background - don't await, let it run async
          generateDiagramFromSimSteps(simulation).catch((error) => {
            logger.error("Error during diagram generation:", error);
          });
        }
      } else {
        logger.debug.info(
          "DEBUGGING_AI_QUALITY: Simulation Generation - No Generation Needed",
          {
            simulationName: simulation.name,
            isAIGenerated: simulation.isAIGenerated,
            hasKeywords: !!simulation.keywords,
            hasSteps: simulation.simSteps.length > 0,
            timestamp: new Date().toISOString(),
          }
        );
      }
    },
    [
      isGeneratingDiagram,
      // Only include isGeneratingDiagram dependency - all other state accessed via useStore.getState()
      // This minimizes function recreations while maintaining necessary generation state check
    ]
  );

  // Changes the currentStep simulation object to the selected simulation
  useEffect(() => {
    // Reset manual toggle flag when switching simulations
    useStore.getState().setUserManuallyToggledSDMaximize(false);

    if (selectedSimulationKey) {
      // Scroll to top of simulations container when a simulation is selected
      const simulationsContainer = document.querySelector(
        ".simulationsCardsContainer"
      );
      if (simulationsContainer) {
        simulationsContainer.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }

      // Get the selected simulation from the simulations store (now includes AI-generated ones)
      const selectedSimulation =
        useStore.getState()?.simulationsState?.simulations[
          selectedSimulationKey
        ];
      if (selectedSimulation) {
        useStore
          .getState()
          .setCurrentUnsavedSimulationObject(selectedSimulation);

        // Trigger simulation generation workflow if needed
        triggerSimulationGeneration(selectedSimulation);
      } else {
        // No action needed when simulation not found
      }
    } else {
      // Reset to initial state when no simulation is selected
      useStore
        .getState()
        .setCurrentUnsavedSimulationObject(INITIAL_MODIFIED_SIMULATION_STATE);
      // Reset running state when closing simulation
      setIsRunning(false);
      // Reset manual toggle flag when closing simulation
      useStore.getState().setUserManuallyToggledSDMaximize(false);
    }
  }, [selectedSimulationKey, triggerSimulationGeneration]);

  // UseEffect to set current step when is editing is enabled to the first step if any
  useEffect(() => {
    if (
      !currentStep &&
      currentUnsavedSimulationObject.simSteps.length > 0 &&
      currentUnsavedSimulationObject?.name === selectedSimulationKey
    ) {
      setCurrentStep(currentUnsavedSimulationObject.simSteps[0].simStepId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, currentUnsavedSimulationObject, selectedSimulationKey]);

  // UseEffect to set current step when selectedCell is changed (when cell selected changes)
  // If there are multiple simsteps have the same selectedCell, it will set the currentStep to first one after the currentStep
  useEffect(() => {
    if (
      currentUnsavedSimulationObject.simSteps.length > 0 &&
      currentStep &&
      selectedSimulationKey
    ) {
      const diagramNodeId = currentUnsavedSimulationObject.simSteps.find(
        (step) => step.simStepId === currentStep
      )?.diagramNodeId;

      if (selectedCell === diagramNodeId) {
        return;
      }

      let currentStepIndex = currentUnsavedSimulationObject.simSteps.findIndex(
        (step) => step.simStepId === currentStep
      );
      let nextStepIndex = currentUnsavedSimulationObject.simSteps.findIndex(
        (step) =>
          step.diagramNodeId === selectedCell &&
          currentUnsavedSimulationObject.simSteps.indexOf(step) >
            currentStepIndex
      );
      let newIdx = null;
      if (nextStepIndex !== -1) {
        newIdx = nextStepIndex;
      } else {
        // loop backwards from currentStep to 0 and set the selectedCell to the closest one from the currentStep
        for (let i = currentStepIndex; i >= 0; i--) {
          if (
            currentUnsavedSimulationObject.simSteps[i].diagramNodeId ===
            selectedCell
          ) {
            newIdx = i;
            break;
          }
        }
      }
      if (newIdx === null) {
        return;
      }
      setCurrentStep(currentUnsavedSimulationObject.simSteps[newIdx].simStepId);
      const simStepsList = document.getElementsByClassName("SimStepsList")[0];
      // them scroll the one that's found into view
      simStepsList.scrollTo({
        top: 36 * newIdx - simStepsList.clientHeight / 2,
        behavior: "smooth",
      });

      // Trigger enhanced code scrolling to ensure highlighted code is visible
      setTimeout(() => {
        useStore.getState().triggerEnhancedCodeScroll();
      }, 200); // Slightly longer delay for cell selection changes
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUnsavedSimulationObject, selectedCell, selectedSimulationKey]);

  // Show welcome dialog when simulations are loaded and contain "Codebase Overview"
  useEffect(() => {
    if (!hasShownWelcomeDialog && simulations?.["Codebase Overview"]) {
      setDialog("WELCOME_DIALOG");
      setHasShownWelcomeDialog(true);
    }
  }, [simulations, hasShownWelcomeDialog, setDialog, setHasShownWelcomeDialog]);

  const handleSimulationCardClick = useCallback(
    async (simulation: Simulation) => {
      // Prevent multiple clicks during generation
      if (isGeneratingDiagram) {
        return;
      }

      try {
        // Check if this is the first AI simulation click during onboarding
        const { shouldShowSimulationOnboarding, firstAISimulationName } =
          useStore.getState();
        const isOnboardingFirstSimulationClick =
          shouldShowSimulationOnboarding &&
          simulation.name === firstAISimulationName &&
          !selectedSimulationKey; // Only if not already selected

        // If the clicked simulation is already selected, close it
        if (selectedSimulationKey === simulation.name) {
          await setSelectedSimulationKey("");
          return;
        }

        // Always set the selected simulation first, regardless of AI generation needs
        await setSelectedSimulationKey(simulation.name);

        // If this is the onboarding first simulation click, trigger the help button step
        if (isOnboardingFirstSimulationClick) {
          // Small delay to let the selection state settle, then start the next onboarding step
          setTimeout(() => {
            useStore.getState().setIsSimulationOnboardingActive(true);
            logger.debug.info(
              "Onboarding: First simulation selected, ready to show help button step"
            );
          }, 500);
        }

        // Scroll to top of simulations container to show the selected simulation
        const simulationsContainer = document.querySelector(
          ".simulationsCardsContainer"
        );
        if (simulationsContainer) {
          simulationsContainer.scrollTo({
            top: 0,
            behavior: "smooth",
          });
        }

        // Get the simulation data
        const selectedSimulation = simulations[simulation.name];
        if (!selectedSimulation) {
          return;
        }

        // The triggerSimulationGeneration function will be called automatically
        // by the useEffect when selectedSimulationKey changes, so no need to duplicate logic here
      } catch (error) {
        setIsGeneratingDiagram(false);
        setDiagramGenerationStartTime(undefined);
        logger.error("Error in handleSimulationCardClick:", error);
      }
    },
    [
      simulations,
      selectedSimulationKey,
      setSelectedSimulationKey,
      isGeneratingDiagram,
      setIsGeneratingDiagram,
      setDiagramGenerationStartTime,
    ]
  );

  // Log when currentUnsavedSimulationObject changes in context
  useEffect(() => {
    logger.debug.info("currentUnsavedSimulationObject context value changed", {
      simulationName: currentUnsavedSimulationObject?.name || "none",
      simStepsCount: currentUnsavedSimulationObject?.simSteps?.length || 0,
      isAIGenerated: currentUnsavedSimulationObject?.isAIGenerated || false,
      selectedSimulationKey,
    });
  }, [currentUnsavedSimulationObject]);

  // Sort simulations to show selected one first, then non-empty simulations, then separate AI-generated from manual
  const sortedSimulationEntries = Object.entries(simulations).sort(
    ([keyA, simA], [keyB, simB]) => {
      if (keyA === selectedSimulationKey) return -1;
      if (keyB === selectedSimulationKey) return 1;

      // Prioritize simulations with at least 1 step (non-empty simulations)
      const hasStepsA = simA.simSteps && simA.simSteps.length > 0;
      const hasStepsB = simB.simSteps && simB.simSteps.length > 0;

      if (hasStepsA && !hasStepsB) return -1;
      if (!hasStepsA && hasStepsB) return 1;

      // Group AI-generated simulations together (but after selected and non-empty)
      if (simA.isAIGenerated && !simB.isAIGenerated) return 1;
      if (!simA.isAIGenerated && simB.isAIGenerated) return -1;

      return keyA.localeCompare(keyB);
    }
  );

  // Add cleanup when closing simulation
  useEffect(() => {
    // Abort diagram generation when simulation is closed
    if (!simulationsState.isEditingSimulations) {
      abortDiagramGeneration();
    }
  }, [simulationsState.isEditingSimulations]);

  return (
    <>
      <SimulationsContext.Provider
        value={{
          isRunning,
          isNewSimulation,
          isMaxSD,
          setIsMaxSD,
          currentStep,
          prev,
          selectedTab,
          isSettingSimulationName,
          simulationNameError,
          currentUnsavedSimulationObject,
          wiki,
          simulationDescription,
          setWiki,
          setIsSettingSimulationName,
          setSimulationNameError,
          setPrev,
          setCurrentStep,
          setIsRunning,
          setIsNewSimulation,
          saveModifiedSimulation,
          setSimulationDescription,
          handleCreateSimulation,
        }}
      >
        <div
          className="simulationsContainer"
          style={
            isSimulationFullSizeMode
              ? {
                  position: "fixed",
                  zIndex: 999999999,
                  top: "0px",
                  height: "100vh",
                  backgroundColor: "white",
                  width: "100%",
                  left: 0,
                }
              : {}
          }
        >
          {isEditingSimulations
            ? (() => {
                return (
                  <>
                    <SimulationsEditNameDelete />
                    <SimulationsControls />
                    <SimulationsBody />
                  </>
                );
              })()
            : (() => {
                return (
                  <>
                    <SimulationsControls />
                    <div
                      className="simulationsCardsContainer"
                      style={{
                        height: isSimulationFullSizeMode ? "100%" : "90%",
                        width: "100%",
                        overflowY: "auto",
                        overflowX: "hidden",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        paddingBottom: "80px", // Increased padding to ensure last card is fully visible
                        paddingRight: "15px",
                        boxSizing: "border-box",
                      }}
                    >
                      {Object.keys(simulations).length === 0 &&
                      !isSettingSimulationName ? (
                        // Show create simulation button when no simulations exist
                        <>
                          <CustomUsecaseItem />
                          <div
                            className="bigGreyButton"
                            onClick={handleCreateSimulation}
                            data-testid="create-simulation-button"
                            style={{ width: "92%" }}
                          >
                            <Typography variant="h6" fontWeight={"thin"}>
                              + Create Simulation
                            </Typography>
                            {!Object.keys(simulations).includes(
                              "Codebase Overview"
                            ) && (
                              <Typography
                                variant="subtitle2"
                                className="createSimulationHint"
                              >
                                Create simulation with name: "Codebase Overview"
                                to create a default simulation for new users!
                              </Typography>
                            )}
                          </div>
                        </>
                      ) : isSettingSimulationName ? (
                        // Show SimulationsBody when setting a new simulation name
                        <SimulationsBody />
                      ) : (
                        // Show simulation cards with conditional CustomUsecaseItem placement
                        <>
                          {(() => {
                            const selectedEntry = sortedSimulationEntries.find(
                              ([key]) => key === selectedSimulationKey
                            );
                            const nonSelectedEntries =
                              sortedSimulationEntries.filter(
                                ([key]) => key !== selectedSimulationKey
                              );

                            return (
                              <>
                                {/* If simulation is selected, show it first */}
                                {selectedEntry && (
                                  <React.Fragment key={selectedEntry[0]}>
                                    <SimulationCard
                                      simulation={selectedEntry[1]}
                                      simulationKey={selectedEntry[0]}
                                      selected={true}
                                      onClick={() =>
                                        !isTransitioning &&
                                        handleSimulationCardClick(
                                          selectedEntry[1]
                                        )
                                      }
                                      disabled={isTransitioning}
                                    />
                                    <SimulationBodyCard
                                      isExpanded={true}
                                      isGeneratingDiagram={isGeneratingDiagram}
                                      diagramGenerationStartTime={
                                        diagramGenerationStartTime
                                      }
                                    />
                                  </React.Fragment>
                                )}

                                {/* CustomUsecaseItem - second when simulation selected, first when none selected */}
                                <CustomUsecaseItem />

                                {/* Render remaining (non-selected) simulations */}
                                {nonSelectedEntries.map(([key, simulation]) => (
                                  <SimulationCard
                                    key={key}
                                    simulation={simulation}
                                    simulationKey={key}
                                    selected={false}
                                    onClick={() =>
                                      !isTransitioning &&
                                      handleSimulationCardClick(simulation)
                                    }
                                    disabled={isTransitioning}
                                  />
                                ))}
                              </>
                            );
                          })()}

                          {/* Generate Usecases with LLM button - shown only if no AI-generated simulations exist */}
                          {showGenerateButton && (
                            <div
                              className="bigGreyButton"
                              style={{
                                cursor: "pointer",
                                marginTop: "20px",
                                marginBottom: "10px",
                                width: "92%",
                              }}
                              onClick={handleGenerateUsecasesClick}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  marginBottom: "4px",
                                }}
                              >
                                <AutoAwesomeIcon
                                  style={{
                                    width: "20px",
                                    height: "20px",
                                    marginRight: "8px",
                                    animation:
                                      "ai-pulse 2s ease-in-out infinite",
                                  }}
                                />
                                <Typography variant="h6" fontWeight={"thin"}>
                                  Generate Usecases with LLM
                                </Typography>
                              </div>
                              <Typography variant="subtitle2">
                                Generate a walk through for the main application
                                features/usecases
                              </Typography>
                            </div>
                          )}

                          {/* Add simulation button at the bottom of the list */}
                          <div
                            className="addSimulationCard"
                            onClick={handleCreateSimulation}
                            style={{
                              width: "92%",
                              minHeight: "50px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: "15px",
                              border: "1px dashed #ffaea6",
                              marginTop: "20px",
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = "#fff5f4";
                              e.currentTarget.style.borderColor = "#ff9088";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor =
                                "transparent";
                              e.currentTarget.style.borderColor = "#ffaea6";
                            }}
                          >
                            <AddRoundedIcon
                              style={{
                                width: "24px",
                                height: "24px",
                                fill: theme.custom.lighterPink,
                                marginRight: "8px",
                              }}
                            />
                            <Typography
                              variant="body1"
                              style={{ color: theme.custom.lighterPink }}
                            >
                              Add New Simulation
                            </Typography>
                          </div>
                        </>
                      )}
                    </div>
                  </>
                );
              })()}
        </div>
      </SimulationsContext.Provider>
    </>
  );
};

export default SimulationsTab;
